import { useState, useCallback, useEffect } from "react";
import { Title, Text, Button, ActionIcon, Empty, Popover } from "rizzui";
import CreateReportForm from "@/app/app-shared/enquiry-entrties/forms/CreateReportForm";
import EnquiryEntriesReportRepository from "@/utilities/repositories/EnquiryEntriesReport";
import { EnquiryReportDTO } from "@/utilities/types/EnquiryReport";
import { ListResponseData } from "@/utilities/types/requests";
import { PiPlus, PiTrash, PiPencil, PiFileText } from "react-icons/pi";
import toast from "react-hot-toast";
import convertToSingaporeTime from "@/utilities/functions/convertToSingaporeTime";

function ReportPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [reports, setReports] = useState<EnquiryReportDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingReport, setEditingReport] = useState<EnquiryReportDTO | null>(
    null
  );

  // Fetch all reports
  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: ListResponseData<EnquiryReportDTO> =
        await EnquiryEntriesReportRepository.getAllReports();
      const sortedReports = (response.data.items || []).sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setReports(sortedReports);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to fetch reports");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete report
  const handleDeleteReport = useCallback(
    async (reportId: string) => {
      try {
        await EnquiryEntriesReportRepository.deleteReport(reportId);
        toast.success("Report deleted successfully");
        await fetchReports();
      } catch (error) {
        console.error("Error deleting report:", error);
        toast.error("Failed to delete report");
      }
    },
    [fetchReports]
  );

  const handleOpenCreateModal = () => {
    setEditingReport(null);
    setIsCreateModalOpen(true);
  };

  const handleOpenEditModal = (report: EnquiryReportDTO) => {
    setEditingReport(report);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleReportSuccess = async () => {
    await fetchReports();
  };

  // Load reports on component mount
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
            Reports
          </Title>
          <Text className="text-gray-600 mt-1">
            Manage and create enquiry reports
          </Text>
        </div>
        <Button
          onClick={handleOpenCreateModal}
          className="flex items-center gap-2"
        >
          <PiPlus className="h-4 w-4" />
          Create Report
        </Button>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center">
            <Text className="text-gray-500">Loading reports...</Text>
          </div>
        ) : reports.length === 0 ? (
          <div className="p-12">
            <Empty
              image={<PiFileText className="h-16 w-16 text-gray-300" />}
              text="No reports found"
              textClassName="text-gray-500"
            >
              <Button
                onClick={handleOpenCreateModal}
                variant="outline"
                className="mt-4"
              >
                <PiPlus className="h-4 w-4 mr-2" />
                Create your first report
              </Button>
            </Empty>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {reports.map((report) => (
              <div
                key={report.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Title
                        as="h3"
                        className="text-lg font-medium text-gray-900"
                      >
                        Report #{report.reportID}
                      </Title>
                    </div>

                    <Text className="text-gray-600 mb-3">
                      {report.description}
                    </Text>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <Text className="text-sm font-medium text-gray-700 mb-1">
                          Emails ({report.emails.length})
                        </Text>
                        <div className="space-y-1">
                          {report.emails.slice(0, 2).map((email, index) => (
                            <Text key={index} className="text-sm text-gray-600">
                              {email}
                            </Text>
                          ))}
                          {report.emails.length > 2 && (
                            <Text className="text-sm text-gray-500">
                              +{report.emails.length - 2} more
                            </Text>
                          )}
                        </div>
                      </div>

                      <div>
                        <Text className="text-sm font-medium text-gray-700 mb-1">
                          Phones ({report.phones.length})
                        </Text>
                        <div className="space-y-1">
                          {report.phones.slice(0, 2).map((phone, index) => (
                            <Text key={index} className="text-sm text-gray-600">
                              {phone}
                            </Text>
                          ))}
                          {report.phones.length > 2 && (
                            <Text className="text-sm text-gray-500">
                              +{report.phones.length - 2} more
                            </Text>
                          )}
                        </div>
                      </div>

                      <div>
                        <Text className="text-sm font-medium text-gray-700 mb-1">
                          Aliases ({report.alias.length})
                        </Text>
                        <div className="space-y-1">
                          {report.alias.slice(0, 2).map((alias, index) => (
                            <Text key={index} className="text-sm text-gray-600">
                              {alias}
                            </Text>
                          ))}
                          {report.alias.length > 2 && (
                            <Text className="text-sm text-gray-500">
                              +{report.alias.length - 2} more
                            </Text>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>
                        Created:{" "}
                        {convertToSingaporeTime(new Date(report.createdAt))}
                      </span>
                      <span>
                        Updated:{" "}
                        {convertToSingaporeTime(new Date(report.updatedAt))}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <ActionIcon
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenEditModal(report)}
                      className="text-gray-600 hover:text-blue-600"
                      title="Edit Report"
                    >
                      <PiPencil className="h-4 w-4" />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      variant="outline"
                      className="text-gray-600 hover:text-red-600"
                      title="Delete Report"
                    >
                      <Popover showArrow={false}>
                        <Popover.Trigger>
                          <Button variant="text" color="danger">
                            <PiTrash className="h-4 w-4" />
                          </Button>
                        </Popover.Trigger>
                        <Popover.Content>
                          {({ setOpen }) => (
                            <div className="w-56">
                              <Title as="h6">Delete the report</Title>
                              <Text>
                                Are you sure you want to delete the report?
                              </Text>
                              <div className="flex justify-end gap-3 mb-1">
                                <Button size="sm" variant="outline">
                                  No
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setOpen(false);
                                    handleDeleteReport(report.id);
                                  }}
                                >
                                  Yes
                                </Button>
                              </div>
                            </div>
                          )}
                        </Popover.Content>
                      </Popover>
                    </ActionIcon>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Report Modal */}
      <CreateReportForm
        open={isCreateModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleReportSuccess}
        editData={editingReport}
        title={editingReport ? "Edit Report" : "Create Report"}
      />
    </div>
  );
}

export default ReportPage;
